import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from 'context/auth'
import BriefCaseIcon from 'icons/brief-case'
import Dialoguemodal from 'components/dialoguemodal'
import Loader from 'components/loader'

// Sub-components
import RestaurantProducts from './products/products'
import RestaurantCategories from './categories/categories'
import RestaurantBanners from './banners/banners'
import RestaurantDeals from './deals/deals'
import RestaurantOrders from './orders/orders'
import RestaurantCustomers from './customers/customers'
import RestaurantBranches from './branches/branches'
import RestaurantSettings from './settings/settings'
import RestaurantSeo from './seo/seo'
import RestaurantAnalytics from './analytics/analytics'
import WebsiteStyle from './styles/website-style'

import { settingsApi, analyticsApi } from './restaurant.api'
import { currencyFormat, formateDate } from 'utils'

// Icons
import {
  FiSmartphone,
  FiTablet,
  FiMonitor,
  FiShoppingBag,
  FiImage,
  FiPercent,
  FiFolder,
  FiMapPin,
  FiGlobe,
  FiChevronRight,
  FiTrendingUp,
  FiClipboard,
  FiSettings,
  FiUsers,
  FiLayout,
  FiRefreshCw,
  FiArrowRight
} from 'react-icons/fi'

// Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell
} from 'recharts'

const STATUS_TONES = {
  Pending: 'tw-bg-[#FBF1DE] tw-text-[#7F5F1E]',
  Confirmed: 'tw-bg-[#FFEDE7] tw-text-[#C4341A]',
  Preparing: 'tw-bg-[#FFEDE7] tw-text-[#C4341A]',
  'Out For Delivery': 'tw-bg-[#F1E7EE] tw-text-[#6E4159]',
  Delivered: 'tw-bg-[#E6F0E9] tw-text-[#3F7A56]',
  Cancelled: 'tw-bg-[#FBE9E7] tw-text-[#C0392B]',
}

function RestaurantHub() {
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [previewDevice, setPreviewDevice] = useState('desktop') // 'mobile', 'tablet', 'desktop'
  const [previewKey, setPreviewKey] = useState(0)
  const [activeModal, setActiveModal] = useState(null) // 'design', 'customer', 'settings', 'orders'
  const [customizerView, setCustomizerView] = useState('preview') // 'preview', 'products', 'banners', 'deals', 'categories', 'branches', 'seo', 'websiteStyle'
  
  // Dashboard statistics
  const [stats, setStats] = useState({
    recentOrders: [],
    totalOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    totalCustomers: 0
  })
  const [monthlySales, setMonthlySales] = useState([])

  const fetchDashboardData = async () => {
    try {
      const settingsRes = await settingsApi.get()
      const dashboardRes = await analyticsApi.dashboard()

      if (settingsRes.response?.data) {
        setSettings(settingsRes.response.data)
      }

      const previewSlugLocal =
        settingsRes.response?.data?.slug || currentUser?.account?._id || currentUser?.account?.id

      const reportsRes = await analyticsApi.reports({ period: 'monthly', scope: 'website', slug: previewSlugLocal })

      if (dashboardRes.response?.data) {
        setStats(dashboardRes.response.data)
      }
      if (reportsRes.response?.data?.monthlySales) {
        setMonthlySales(reportsRes.response.data.monthlySales)
      }
    } catch (err) {
      console.error('Failed to fetch data in hub:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  // DEBUG: log imported component types to help diagnose invalid element errors
  useEffect(() => {
    try {
      // eslint-disable-next-line no-console
      console.debug('RestaurantHub imports:', {
        WebsiteStyle: typeof WebsiteStyle,
        RestaurantAnalytics: typeof RestaurantAnalytics,
        RestaurantSeo: typeof RestaurantSeo,
        RestaurantSettings: typeof RestaurantSettings,
        RestaurantProducts: typeof RestaurantProducts,
        RestaurantCategories: typeof RestaurantCategories,
        RestaurantBanners: typeof RestaurantBanners,
        RestaurantDeals: typeof RestaurantDeals,
        RestaurantOrders: typeof RestaurantOrders,
        RestaurantCustomers: typeof RestaurantCustomers,
        RestaurantBranches: typeof RestaurantBranches,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed logging component types in RestaurantHub', err)
    }
  }, [])

  // Auto-close sub-modals if path changes
  useEffect(() => {
    setActiveModal(null)
  }, [location.pathname])

  const handleModalClose = () => {
    setActiveModal(null)
    setPreviewKey((k) => k + 1)
    fetchDashboardData()
  }

  const handleCustomizerViewChange = (view) => {
    setCustomizerView(view)
    if (view === 'preview') {
      setPreviewKey((k) => k + 1)
      fetchDashboardData()
    }
  }

  if (loading) {
    return (
      <div className="tw-py-20 tw-flex tw-items-center tw-justify-center">
        <Loader />
      </div>
    )
  }

  const previewSlug = settings?.slug || currentUser?.account?._id || currentUser?.account?.id
  const recentOrders = stats.recentOrders || []

  const tabs = [
    { id: 'design', label: 'Design', icon: <FiLayout className="tw-h-4 tw-w-4" /> },
    { id: 'customer', label: 'Customer', icon: <FiUsers className="tw-h-4 tw-w-4" /> },
    { id: 'settings', label: 'Settings', icon: <FiSettings className="tw-h-4 tw-w-4" /> },
    // { id: 'websiteStyle', label: 'Update Website', icon: <FiGlobe className="tw-h-4 tw-w-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <FiTrendingUp className="tw-h-4 tw-w-4" /> },
  ]

  return (
    <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 tw-py-4">
      {/* Title Header bar */}
      <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-mb-8 tw-border-b tw-border-zinc-200 tw-pb-5">
        <div>
          <h2 className="tw-text-2xl tw-font-black tw-text-zinc-900 tw-tracking-tight tw-flex tw-items-center tw-gap-2.5">
            <BriefCaseIcon className="tw-text-zinc-800" />
            Order Management Hub
          </h2>
          <p className="tw-text-xs tw-text-zinc-400 tw-font-medium tw-mt-1">
            Scoped for organisation: <span className="tw-font-bold tw-text-zinc-650">{currentUser?.account?.organisationName}</span>
          </p>
        </div>
        {previewSlug && (
          <a
            href={`/w/${previewSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-bg-zinc-900 hover:tw-bg-zinc-800 tw-text-white tw-rounded-xl tw-text-xs tw-font-black tw-uppercase tw-tracking-widest tw-transition-all tw-no-underline tw-shadow-sm"
          >
            Open Live Shop
          </a>
        )}
      </div>

      {/* Main split grid - 50/50 Equal Column Division */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
        
        {/* Left Column: Vertical Tabs Menu ONLY */}
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-zinc-200/80 tw-shadow-sm tw-p-6 tw-flex tw-flex-col tw-gap-3">
            <h4 className="tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wider tw-text-zinc-400 tw-mb-2">
              Workspace Options
            </h4>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveModal(tab.id)}
                className="tw-flex tw-items-center tw-gap-3.5 tw-w-full tw-px-5 tw-py-4 tw-bg-zinc-50 hover:tw-bg-zinc-900 tw-text-zinc-700 hover:tw-text-white tw-rounded-xl tw-text-xs tw-font-black tw-uppercase tw-tracking-wider tw-transition-all tw-text-left"
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right Column: Dashboard Feeds (Divided Equally in Height) */}
        <div className="tw-flex tw-flex-col tw-gap-8">
          
          {/* Section 1: Recent Orders */}
          <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-zinc-200/80 tw-shadow-sm tw-p-5 tw-flex tw-flex-col tw-min-h-[280px]">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
              <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-uppercase tw-tracking-wider tw-flex tw-items-center tw-gap-2">
                <FiClipboard className="tw-text-zinc-650" />
                Recent Orders
              </h3>
              <button
                onClick={() => setActiveModal('orders')}
                className="tw-text-[11px] tw-font-extrabold tw-uppercase tw-tracking-wider tw-text-red-500 hover:tw-text-red-650 tw-transition-colors"
              >
                View All
              </button>
            </div>

            {recentOrders.length > 0 ? (
              <div className="tw-flex tw-flex-col tw-gap-3.5 tw-flex-1 tw-justify-center">
                {recentOrders.slice(0, 4).map((order) => (
                  <div
                    key={order._id}
                    className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-border-b tw-border-zinc-100 tw-pb-3 tw-last:border-0 tw-last:pb-0"
                  >
                    <div>
                      <p className="tw-font-mono tw-font-bold tw-text-zinc-900">
                        {order.orderNumber}
                      </p>
                      <p className="tw-text-zinc-450 tw-mt-0.5 tw-font-medium">
                        {order.customerInfo?.name || 'Walk-in'} · {order.createdAt ? formateDate(order.createdAt) : ''}
                      </p>
                    </div>
                    <div className="tw-text-right">
                      <p className="tw-font-black tw-text-zinc-900">
                        {currencyFormat(order.totalAmount || 0)}
                      </p>
                      <span
                        className={`tw-inline-block tw-text-[9px] tw-font-black tw-px-2 tw-py-0.5 tw-rounded-full tw-uppercase tw-mt-1 ${STATUS_TONES[order.status] || 'tw-bg-zinc-100 tw-text-zinc-500'}`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center">
                <p className="tw-text-xs tw-text-zinc-400 tw-py-6">
                  No recent orders placed.
                </p>
              </div>
            )}
          </div>

          {/* Section 2: Sales Graph */}
          <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-zinc-200/80 tw-shadow-sm tw-p-5 tw-flex tw-flex-col tw-min-h-[280px]">
            <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-uppercase tw-tracking-wider tw-mb-4 tw-flex tw-items-center tw-gap-2">
              <FiTrendingUp className="tw-text-zinc-650" />
              Monthly Sales
            </h3>
            
            {monthlySales.length > 0 && (monthlySales.reduce((s, m) => s + (m.sales || 0), 0) > 0) && ((stats.totalRevenue || 0) > 0) ? (
              <div className="tw-flex-1 tw-w-full tw-h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlySales} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis dataKey="name" fontSize={10} stroke="#a1a1aa" />
                    <YAxis fontSize={10} stroke="#a1a1aa" />
                    <ChartTooltip 
                      formatter={(value) => currencyFormat(value)}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px', fontSize: '11px' }}
                    />
                    <Bar dataKey="sales" radius={[4, 4, 0, 0]}>
                      {monthlySales.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === monthlySales.length - 1 ? '#ef4444' : '#18181b'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center">
                <p className="tw-text-xs tw-text-zinc-400 tw-py-6">
                  No sales reports recorded yet.
                </p>
              </div>
            )}
          </div>

        </div>

      </div>

      {/* Modal Backdrop overlay */}
      {activeModal && (
        <div 
          onClick={handleModalClose}
          className="tw-fixed tw-inset-0 tw-z-40 tw-bg-black/60 tw-backdrop-blur-sm tw-transition-opacity"
        />
      )}

      {/* 1. Design / Storefront Customizer Modal (Centered overlay popup, NOT full screen) */}
      {activeModal === 'design' && (
        <div className="tw-fixed tw-inset-8 tw-z-50 tw-bg-zinc-50 tw-rounded-3xl tw-shadow-2xl tw-flex tw-flex-col tw-border tw-border-zinc-200 tw-overflow-hidden tw-animate-fadeIn">
          {/* Customizer Header */}
          <div className="tw-bg-white tw-border-b tw-border-zinc-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-4">
              <button
                onClick={handleModalClose}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-text-zinc-605 hover:tw-text-zinc-950 tw-text-xs tw-font-extrabold tw-transition-colors"
              >
                <FiArrowRight className="tw-h-4 tw-w-4 tw-rotate-180" /> Back to Hub
              </button>
              <div className="tw-h-5 tw-w-[1px] tw-bg-zinc-200" />
              <div>
                <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-tracking-tight tw-uppercase">
                  Live Storefront Customizer
                </h3>
              </div>
            </div>

            {/* Device Selectors (Only show in live preview mode) */}
            {customizerView === 'preview' ? (
              <div className="tw-flex tw-items-center tw-bg-zinc-100 tw-p-1 tw-rounded-xl">
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`tw-p-2 tw-rounded-lg tw-transition-all ${
                    previewDevice === 'mobile'
                      ? 'tw-bg-white tw-text-zinc-900 tw-shadow-sm'
                      : 'tw-text-zinc-400 hover:tw-text-zinc-650'
                  }`}
                  title="Mobile View"
                >
                  <FiSmartphone className="tw-h-4 tw-w-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('tablet')}
                  className={`tw-p-2 tw-rounded-lg tw-transition-all ${
                    previewDevice === 'tablet'
                      ? 'tw-bg-white tw-text-zinc-900 tw-shadow-sm'
                      : 'tw-text-zinc-400 hover:tw-text-zinc-650'
                  }`}
                  title="Tablet View"
                >
                  <FiTablet className="tw-h-4 tw-w-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`tw-p-2 tw-rounded-lg tw-transition-all ${
                    previewDevice === 'desktop'
                      ? 'tw-bg-white tw-text-zinc-900 tw-shadow-sm'
                      : 'tw-text-zinc-400 hover:tw-text-zinc-650'
                  }`}
                  title="Desktop View"
                >
                  <FiMonitor className="tw-h-4 tw-w-4" />
                </button>
              </div>
            ) : (
              <div className="tw-text-xs tw-text-zinc-400 tw-font-bold tw-uppercase tw-tracking-wider">
                Managing: {customizerView}
              </div>
            )}

            {/* Top Right Actions */}
            <div className="tw-flex tw-items-center tw-gap-3">
              {customizerView === 'preview' && (
                <button
                  onClick={() => setPreviewKey((k) => k + 1)}
                  className="tw-p-2.5 tw-bg-zinc-100 hover:tw-bg-zinc-200 tw-text-zinc-600 hover:tw-text-zinc-950 tw-rounded-xl tw-transition-all"
                  title="Reload preview"
                >
                  <FiRefreshCw className="tw-h-4 tw-w-4" />
                </button>
              )}
              {previewSlug && (
                <a
                  href={`/w/${previewSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-bg-zinc-900 hover:tw-bg-zinc-800 tw-text-white tw-rounded-xl tw-text-xs tw-font-black tw-uppercase tw-tracking-widest tw-transition-all tw-no-underline"
                >
                  Live Shop
                </a>
              )}
            </div>
          </div>

          {/* Customizer Sidebar & Canvas Area */}
          <div className="tw-flex-1 tw-flex tw-overflow-hidden">
            
            {/* Sidebar Controls (Left) */}
            <div className="tw-w-64 tw-bg-white tw-border-r tw-border-zinc-200 tw-p-5 tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto">
              <h4 className="tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wider tw-text-zinc-400 tw-mb-3">
                Workspace Panels
              </h4>
              <button
                onClick={() => handleCustomizerViewChange('preview')}
                className={`tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'preview'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700'
                }`}
              >
                <FiLayout className="tw-h-4 tw-w-4" />
                Live Preview
              </button>

              <h4 className="tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wider tw-text-zinc-400 tw-mt-4 tw-mb-3">
                Store Content Elements
              </h4>
              <button
                onClick={() => handleCustomizerViewChange('products')}
                className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'products'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700 tw-border tw-border-zinc-200/40'
                }`}
              >
                <span className="tw-flex tw-items-center tw-gap-2.5">
                  <FiShoppingBag className="tw-h-4 tw-w-4" />
                  Products
                </span>
                <FiChevronRight className="tw-h-4 tw-w-4" />
              </button>
              <button
                onClick={() => handleCustomizerViewChange('banners')}
                className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'banners'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700 tw-border tw-border-zinc-200/40'
                }`}
              >
                <span className="tw-flex tw-items-center tw-gap-2.5">
                  <FiImage className="tw-h-4 tw-w-4" />
                  Banners
                </span>
                <FiChevronRight className="tw-h-4 tw-w-4" />
              </button>
              <button
                onClick={() => handleCustomizerViewChange('deals')}
                className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'deals'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700 tw-border tw-border-zinc-200/40'
                }`}
              >
                <span className="tw-flex tw-items-center tw-gap-2.5">
                  <FiPercent className="tw-h-4 tw-w-4" />
                  Deals & Coupons
                </span>
                <FiChevronRight className="tw-h-4 tw-w-4" />
              </button>
              <button
                onClick={() => handleCustomizerViewChange('categories')}
                className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'categories'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700 tw-border tw-border-zinc-200/40'
                }`}
              >
                <span className="tw-flex tw-items-center tw-gap-2.5">
                  <FiFolder className="tw-h-4 tw-w-4" />
                  Categories
                </span>
                <FiChevronRight className="tw-h-4 tw-w-4" />
              </button>
              <button
                onClick={() => handleCustomizerViewChange('branches')}
                className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'branches'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700 tw-border tw-border-zinc-200/40'
                }`}
              >
                <span className="tw-flex tw-items-center tw-gap-2.5">
                  <FiMapPin className="tw-h-4 tw-w-4" />
                  Locations
                </span>
                <FiChevronRight className="tw-h-4 tw-w-4" />
              </button>
              <button
                onClick={() => handleCustomizerViewChange('seo')}
                className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-extrabold tw-transition-all tw-text-left ${
                  customizerView === 'seo'
                    ? 'tw-bg-zinc-900 tw-text-white'
                    : 'tw-bg-zinc-50 hover:tw-bg-zinc-100 tw-text-zinc-700 tw-border tw-border-zinc-200/40'
                }`}
              >
                <span className="tw-flex tw-items-center tw-gap-2.5">
                  <FiGlobe className="tw-h-4 tw-w-4" />
                  SEO Settings
                </span>
                <FiChevronRight className="tw-h-4 tw-w-4" />
              </button>
            </div>

            {/* Manager Canvas (Center/Right) - Components rendered inline */}
            <div className="tw-flex-1 tw-bg-zinc-150 tw-p-6 tw-overflow-y-auto">
              {customizerView === 'preview' ? (
                <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                  {previewSlug ? (
                    <div 
                      className="tw-transition-all tw-duration-300 tw-shadow-xl tw-relative"
                      style={{
                        width: previewDevice === 'mobile' ? '375px' : previewDevice === 'tablet' ? '768px' : '100%',
                        height: previewDevice === 'mobile' ? '667px' : previewDevice === 'tablet' ? '1024px' : '100%',
                        maxHeight: '100%',
                        border: previewDevice !== 'desktop' ? '12px solid #18181b' : '1px solid #e4e4e7',
                        borderRadius: previewDevice === 'mobile' ? '36px' : previewDevice === 'tablet' ? '28px' : '12px',
                        backgroundColor: '#fff',
                        overflow: 'hidden',
                      }}
                    >
                      {previewDevice === 'mobile' && (
                        <div className="tw-absolute tw-top-0 tw-left-1/2 -tw-translate-x-1/2 tw-w-28 tw-h-5 tw-bg-zinc-900 tw-rounded-b-2xl tw-z-30" />
                      )}

                      <iframe
                        key={previewKey}
                        src={`/w/${previewSlug}?preview=true`}
                        title="Live Storefront Preview Frame"
                        className="tw-w-full tw-h-full"
                        style={{ border: 'none' }}
                      />
                    </div>
                  ) : (
                    <div className="tw-text-center tw-text-zinc-400 tw-text-xs">
                      Loading preview framing...
                    </div>
                  )}
                </div>
              ) : (
                <div className="tw-bg-white tw-rounded-3xl tw-shadow-sm tw-p-6 tw-min-h-full">
                  {customizerView === 'products' && <RestaurantProducts />}
                  {customizerView === 'categories' && <RestaurantCategories />}
                  {customizerView === 'banners' && <RestaurantBanners />}
                  {customizerView === 'deals' && <RestaurantDeals />}
                  {customizerView === 'branches' && <RestaurantBranches />}
                  {customizerView === 'seo' && <RestaurantSeo />}
                  {customizerView === 'websiteStyle' && <WebsiteStyle />}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* 2. Customer Directory Modal (Centered overlay popup, NOT full screen) */}
      {activeModal === 'customer' && (
        <div className="tw-fixed tw-inset-16 tw-z-50 tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-flex tw-flex-col tw-border tw-border-zinc-200 tw-overflow-hidden tw-animate-fadeIn">
          <div className="tw-bg-white tw-border-b tw-border-zinc-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-tracking-tight tw-uppercase">
              Customer Directory
            </h3>
            <button
              onClick={handleModalClose}
              className="tw-text-zinc-400 hover:tw-text-zinc-900 tw-text-xs tw-font-extrabold"
            >
              Close
            </button>
          </div>
          <div className="tw-flex-1 tw-p-6 tw-overflow-y-auto">
            <RestaurantCustomers />
          </div>
        </div>
      )}

      {/* 3. Settings Form Modal (Centered overlay popup, NOT full screen) */}
      {activeModal === 'settings' && (
        <div className="tw-fixed tw-inset-16 tw-z-50 tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-flex tw-flex-col tw-border tw-border-zinc-200 tw-overflow-hidden tw-animate-fadeIn">
          <div className="tw-bg-white tw-border-b tw-border-zinc-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-tracking-tight tw-uppercase">
              Storefront Settings
            </h3>
            <button
              onClick={handleModalClose}
              className="tw-text-zinc-400 hover:tw-text-zinc-900 tw-text-xs tw-font-extrabold"
            >
              Close
            </button>
          </div>
          <div className="tw-flex-1 tw-p-6 tw-overflow-y-auto">
            <RestaurantSettings />
          </div>
        </div>
      )}

      {activeModal === 'websiteStyle' && (
        <div className="tw-fixed tw-inset-16 tw-z-50 tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-flex tw-flex-col tw-border tw-border-zinc-200 tw-overflow-hidden tw-animate-fadeIn">
          <div className="tw-bg-white tw-border-b tw-border-zinc-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-tracking-tight tw-uppercase">
              Update Your Website
            </h3>
            <button
              onClick={handleModalClose}
              className="tw-text-zinc-400 hover:tw-text-zinc-900 tw-text-xs tw-font-extrabold"
            >
              Close
            </button>
          </div>
          <div className="tw-flex-1 tw-p-6 tw-overflow-y-auto">
            <WebsiteStyle
              onSave={() => {
                fetchDashboardData()
                setPreviewKey((k) => k + 1)
              }}
            />
          </div>
        </div>
      )}

      {/* 4. Orders Manager Modal (Centered overlay popup, NOT full screen) */}
      {activeModal === 'orders' && (
        <div className="tw-fixed tw-inset-16 tw-z-50 tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-flex tw-flex-col tw-border tw-border-zinc-200 tw-overflow-hidden tw-animate-fadeIn">
          <div className="tw-bg-white tw-border-b tw-border-zinc-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-tracking-tight tw-uppercase">
              Order Manager
            </h3>
            <button
              onClick={handleModalClose}
              className="tw-text-zinc-400 hover:tw-text-zinc-900 tw-text-xs tw-font-extrabold"
            >
              Close
            </button>
          </div>
          <div className="tw-flex-1 tw-p-6 tw-overflow-y-auto">
            <RestaurantOrders />
          </div>
        </div>
      )}

      {/* 5. Analytics Modal (Centered overlay popup, NOT full screen) */}
      {activeModal === 'analytics' && (
        <div className="tw-fixed tw-inset-16 tw-z-50 tw-bg-white tw-rounded-3xl tw-shadow-2xl tw-flex tw-flex-col tw-border tw-border-zinc-200 tw-overflow-hidden tw-animate-fadeIn">
          <div className="tw-bg-white tw-border-b tw-border-zinc-200 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-sm tw-font-black tw-text-zinc-900 tw-tracking-tight tw-uppercase">
              Analytics
            </h3>
            <button
              onClick={handleModalClose}
              className="tw-text-zinc-400 hover:tw-text-zinc-900 tw-text-xs tw-font-extrabold"
            >
              Close
            </button>
          </div>
          <div className="tw-flex-1 tw-p-6 tw-overflow-y-auto">
            <RestaurantAnalytics />
          </div>
        </div>
      )}

    </div>
  )
}

export default RestaurantHub
